// iFotoLife — Contenu FR (français, langue par défaut)
import { IMG } from "./images";

const ui = {
  langLabel: "Langue",
  backToMenu: "Menu",
  presenter: "Présentateur",
  presenterNotes: "Notes du présentateur",
  fullscreen: "Plein écran",
  exitFullscreen: "Quitter",
  exportPdf: "Export PDF",
  share: "Partager",
  linkCopied: "Lien copié dans le presse-papier",
  shareTitle: "Partager cette slide",
  shareText: "Aperçu personnalisé pour WhatsApp & LinkedIn. Le lien ouvre directement cette slide.",
  copyLink: "Copier le lien",
  slide: "Slide",
  of: "sur",
  next: "Suivant",
  prev: "Précédent",
  start: "Découvrir",
  hint: "Utilisez ← → pour naviguer · F plein écran · N notes",
  print: "Imprimer",
};

const home = {
  eyebrow: "iFotoLife · Marketplace photo hospitality",
  title: "Vos souvenirs,\nretrouvés en un regard.",
  intro:
    "La présentation commerciale et le tutoriel d'iFotoLife — l'expérience photo premium des plus belles adresses. Choisissez un parcours ci-dessous.",
  marquee: "L'expérience photo premium des plus belles adresses —",
  cardsTitle: "Quatre parcours",
  cards: [
    {
      key: "short",
      index: "01",
      title: "Pitch commercial — Court",
      meta: "10 slides · 5 min",
      desc: "L'essentiel de la proposition de valeur, condensé pour une lecture rapide.",
    },
    {
      key: "full",
      index: "02",
      title: "Pitch commercial — Complet",
      meta: "14 slides · 12 min",
      desc: "La présentation détaillée : problème, solution, marché, modèle et vision.",
    },
    {
      key: "tutorial",
      index: "03",
      title: "Tutoriel utilisateur",
      meta: "8 étapes",
      desc: "L'expérience client, pas à pas, de la recherche au téléchargement HD.",
    },
    {
      key: "onepager",
      index: "04",
      title: "One-page commerciale",
      meta: "Résumé imprimable",
      desc: "Le récapitulatif partageable et prêt à imprimer en un seul écran.",
    },
  ],
  taglines: [
    "L'expérience photo premium des plus belles adresses.",
    "De l'instant capturé au souvenir livré.",
    "Le marketplace photo qui transforme les séjours en revenus.",
  ],
};

const modeMeta = {
  short: "Pitch commercial — Court",
  full: "Pitch commercial — Complet",
  tutorial: "Tutoriel utilisateur",
};

// ---- Slides du pitch complet (14) ----
const s_title = {
  type: "title",
  accent: "gold",
  overline: "Marketplace photo · Hospitality premium",
  title: "Vos souvenirs,\nretrouvés en un regard.",
  lead: "iFotoLife transforme chaque séjour en un souvenir livré — reconnaissance faciale, galerie personnalisée, paiement sécurisé.",
  image: IMG.familyBeach,
  imageAlt: "Famille souriante sur une plage tropicale",
  notes:
    "Ouvrez sur l'émotion : le souvenir. En une phrase, iFotoLife retrouve instantanément les photos d'un client et les lui livre. Positionnez la marque comme premium et hospitality dès la première seconde.",
};

const s_problem_client = {
  type: "problem",
  accent: "coral",
  overline: "Le problème · Côté clients",
  title: "Les plus beaux moments\nse perdent.",
  body:
    "Pendant un séjour, des dizaines de photos professionnelles sont prises — mais les clients ne les retrouvent jamais, ou renoncent devant un processus fastidieux.",
  bullets: [
    { icon: "SearchX", title: "Photos introuvables", text: "Impossible de savoir sur quelle photo on apparaît, parmi des milliers de clichés." },
    { icon: "Clock", title: "Processus long", text: "Attente, e-mails, clés USB : l'expérience casse la magie du moment." },
    { icon: "ImageOff", title: "Souvenirs perdus", text: "Faute d'un accès simple, la majorité des photos ne sont jamais achetées." },
  ],
  image: IMG.familyBeach,
  imageAlt: "Moment de vacances en famille",
  notes:
    "Insistez sur la frustration : le client vit un moment magique, mais le souvenir se perd dans un processus daté. C'est une valeur émotionnelle ET commerciale gâchée.",
};

const s_problem_hotels = {
  type: "problem",
  accent: "coral",
  overline: "Le problème · Hôtels & photographes",
  title: "Un potentiel de revenus\ninexploité.",
  body:
    "Les établissements et les photographes disposent d'un contenu à forte valeur, mais sans outil pour le monétiser simplement et à grande échelle.",
  bullets: [
    { icon: "Building2", title: "Hôtels sans levier", text: "Aucun canal structuré pour valoriser les photos en revenu additionnel." },
    { icon: "Camera", title: "Photographes limités", text: "Vente artisanale, logistique lourde, faible taux de conversion." },
    { icon: "TrendingDown", title: "Marge dissipée", text: "Un actif premium reste non monétisé, séjour après séjour." },
  ],
  image: IMG.photographer,
  imageAlt: "Photographe professionnel en événement",
  notes:
    "Montrez que le problème est aussi économique. Hôtels et photographes possèdent l'actif mais pas l'infrastructure. iFotoLife est cette infrastructure.",
};

const s_solution = {
  type: "solution",
  accent: "gold",
  overline: "La solution",
  title: "iFotoLife :\nle pont entre l'instant\net le souvenir.",
  body:
    "Une marketplace photo clé en main qui relie hôtels, photographes et clients. Le client se reconnaît en un selfie, découvre sa galerie et achète en quelques secondes.",
  bullets: [
    { icon: "ScanFace", title: "Reconnaissance faciale", text: "Le client retrouve ses photos instantanément grâce à un simple selfie." },
    { icon: "Store", title: "Marketplace intégrée", text: "Galerie, panier, paiement et livraison HD dans une expérience unique." },
    { icon: "Handshake", title: "Clé en main", text: "Aucune logistique pour l'hôtel : iFotoLife opère toute la chaîne." },
  ],
  image: IMG.resortPool,
  imageAlt: "Piscine de resort au coucher du soleil",
  notes:
    "C'est le cœur du pitch : iFotoLife résout les deux problèmes d'un coup — émotion côté client, revenu côté établissement. Une plateforme, trois gagnants.",
};

const s_journey_client = {
  type: "steps",
  accent: "gold",
  overline: "Parcours · Client final",
  title: "Quatre gestes,\nun souvenir livré.",
  steps: [
    { n: "1", title: "Scanner le QR", text: "Le client scanne le code présent dans l'établissement ou reçoit un lien." },
    { n: "2", title: "Prendre un selfie", text: "La reconnaissance faciale identifie toutes ses photos en quelques secondes." },
    { n: "3", title: "Découvrir sa galerie", text: "Une sélection personnalisée, prête à être parcourue et choisie." },
    { n: "4", title: "Payer & télécharger", text: "Paiement sécurisé, livraison HD immédiate, souvenir garanti." },
  ],
  mockup: "phone",
  mockImage: IMG.familyBeach,
  notes:
    "Racontez ce parcours comme une histoire fluide. Le client ne fait presque rien : la technologie s'efface derrière l'émotion.",
};

const s_journey_photographer = {
  type: "steps",
  accent: "coral",
  overline: "Parcours · Photographe partenaire",
  title: "Photographier,\nvendre, encaisser.",
  steps: [
    { n: "1", title: "Publier ses photos", text: "Upload rapide, indexation faciale automatique par l'événement." },
    { n: "2", title: "Vente automatisée", text: "iFotoLife gère la vitrine, le panier et le paiement à sa place." },
    { n: "3", title: "Watermark & protection", text: "Aperçu protégé, livraison HD uniquement après achat." },
    { n: "4", title: "Revenus structurés", text: "Suivi des ventes et reversements clairs et automatisés." },
  ],
  mockup: "desktop",
  mockImage: IMG.photographer,
  notes:
    "Le photographe passe d'une vente artisanale à un canal structuré. Insistez sur le gain de temps et la protection de son travail.",
};

const s_value_hotels = {
  type: "stats",
  accent: "gold",
  overline: "Valeur · Hôtels & resorts",
  title: "Un revenu additionnel,\nzéro effort.",
  body:
    "iFotoLife devient une nouvelle ligne de revenu pour l'établissement — sans investissement, sans logistique, sans risque.",
  stats: [
    { value: "+15 %", label: "de revenu annexe par établissement" },
    { value: "0 €", label: "d'investissement initial" },
    { value: "< 3 s", label: "pour retrouver ses photos" },
    { value: "100 %", label: "clé en main & marque hospitality" },
  ],
  image: IMG.hotelLobby,
  imageAlt: "Hall d'hôtel élégant et moderne",
  notes:
    "Ces chiffres sont illustratifs — adaptez-les au prospect. Le message clé : revenu additionnel, zéro effort, zéro risque.",
};

const s_features = {
  type: "features",
  accent: "gold",
  overline: "Fonctionnalités clés",
  title: "Une technologie\nau service de l'émotion.",
  bullets: [
    { icon: "ScanFace", title: "Reconnaissance faciale", text: "Identification instantanée du client sur l'ensemble des clichés." },
    { icon: "GalleryHorizontalEnd", title: "Galerie personnalisée", text: "Une sélection dédiée, élégante, prête à l'achat." },
    { icon: "ShieldCheck", title: "Watermark pro", text: "Aperçus protégés, propriété du photographe préservée." },
    { icon: "CreditCard", title: "Paiement sécurisé", text: "Transaction fluide, conforme et rassurante." },
    { icon: "Download", title: "Livraison HD", text: "Téléchargement immédiat en haute définition après achat." },
    { icon: "QrCode", title: "Accès QR / lien", text: "Point d'entrée simple, sur place ou à distance." },
  ],
  notes:
    "Ne récitez pas la liste : reliez chaque fonctionnalité à un bénéfice. La reconnaissance faciale = plus jamais de photos perdues.",
};

const s_business = {
  type: "business",
  accent: "gold",
  overline: "Modèle économique",
  title: "Une logique\nmarketplace gagnant-gagnant.",
  body:
    "iFotoLife opère la plateforme et partage la valeur générée entre l'établissement, le photographe et la plateforme, à chaque vente.",
  bullets: [
    { icon: "Hotel", title: "Hôtel", text: "Commission sur chaque vente réalisée dans son enceinte, sans effort." },
    { icon: "Camera", title: "Photographe", text: "Rémunéré sur ses ventes, sans gérer la logistique commerciale." },
    { icon: "Layers", title: "iFotoLife", text: "Opère la technologie, le paiement et l'expérience client." },
  ],
  stats: [
    { value: "Commission", label: "modèle transparent par vente" },
    { value: "Récurrent", label: "revenu à chaque séjour" },
  ],
  notes:
    "Le modèle est aligné : personne n'avance de coût, tout le monde gagne à la vente. C'est ce qui facilite l'adoption côté hôtels.",
};

const s_usecases = {
  type: "usecases",
  accent: "gold",
  overline: "Cas d'usage",
  title: "Partout où naissent\nles souvenirs.",
  bullets: [
    { icon: "Palmtree", title: "Resort & plage", text: "Photos de vacances, couchers de soleil, activités : le terrain idéal du souvenir premium." },
    { icon: "Hotel", title: "Hôtel & spa", text: "Séjours d'affaires ou de loisir, moments d'exception à immortaliser." },
    { icon: "PartyPopper", title: "Événement privé", text: "Mariages, séminaires, célébrations : une demande photo forte et concentrée." },
  ],
  image: IMG.resortAerial,
  imageAlt: "Vue aérienne d'un resort de luxe",
  notes:
    "Élargissez l'imaginaire : iFotoLife n'est pas qu'un outil de plage. Adaptez le cas d'usage au profil du prospect en face de vous.",
};

const s_differentiation = {
  type: "list",
  accent: "gold",
  overline: "Différenciation",
  title: "Pourquoi iFotoLife\ns'impose.",
  bullets: [
    { icon: "Sparkles", title: "Expérience premium", text: "Un standard hospitality, aligné sur l'exigence des plus belles adresses." },
    { icon: "Zap", title: "Instantanéité", text: "La reconnaissance faciale supprime la friction du tri manuel." },
    { icon: "PackageCheck", title: "Chaîne complète", text: "De la capture à la livraison HD, une seule plateforme intégrée." },
    { icon: "Lock", title: "Confiance & sécurité", text: "Protection des photos, paiement sécurisé, données maîtrisées." },
  ],
  notes:
    "Positionnez-vous face aux solutions artisanales et aux galeries génériques : iFotoLife combine premium, instantanéité et chaîne complète.",
};

const s_vision = {
  type: "vision",
  accent: "gold",
  overline: "Vision internationale",
  title: "Le standard mondial\ndu souvenir de séjour.",
  body:
    "Multilingue par conception, iFotoLife se déploie établissement après établissement, marché après marché — un réseau de destinations premium unies par une même expérience.",
  stats: [
    { value: "3", label: "langues natives : FR · EN · ES" },
    { value: "∞", label: "destinations activables" },
    { value: "1", label: "expérience, partout identique" },
  ],
  image: IMG.resortAerial,
  imageAlt: "Destination de luxe vue du ciel",
  notes:
    "Projetez l'ambition : iFotoLife vise à devenir un standard international. Le multilingue natif est un atout de déploiement immédiat.",
};

const s_faq = {
  type: "faq",
  accent: "gold",
  overline: "FAQ · Objections fréquentes",
  title: "Vos questions,\nnos réponses.",
  faq: [
    { q: "La reconnaissance faciale est-elle respectueuse de la vie privée ?", a: "Oui : le selfie sert uniquement à retrouver ses propres photos, avec consentement et données maîtrisées." },
    { q: "Quel effort pour l'hôtel ?", a: "Aucun. iFotoLife opère toute la chaîne : mise en place, technologie, paiement, support." },
    { q: "Comment sont protégées les photos ?", a: "Aperçus en watermark, livraison HD uniquement après achat, propriété du photographe préservée." },
    { q: "Combien ça coûte à l'établissement ?", a: "Zéro investissement initial : un modèle par commission, aligné sur les ventes réalisées." },
  ],
  notes:
    "Anticipez les objections classiques : vie privée, effort, protection, coût. Restez factuel et rassurant.",
};

const s_cta = {
  type: "cta",
  accent: "gold",
  overline: "Appel à l'action",
  title: "Faisons de chaque séjour\nun souvenir livré.",
  body:
    "Rejoignez iFotoLife en tant qu'établissement ou photographe partenaire. Activons ensemble un nouveau revenu premium.",
  bullets: [
    { icon: "Mail", title: "Contact", text: "contact@ifotolife.shop" },
    { icon: "Globe", title: "En ligne", text: "ifotolife.shop" },
  ],
  image: IMG.resortPool,
  imageAlt: "Resort premium au coucher du soleil",
  notes:
    "Terminez sur un appel clair et une émotion. Proposez une prochaine étape concrète : démo, activation pilote, contact direct.",
};

// Slides fusionnées pour la version courte
const s_problem_merged = {
  type: "problem",
  accent: "coral",
  overline: "Le problème",
  title: "Des souvenirs perdus,\nun revenu inexploité.",
  body:
    "Côté clients, les photos se perdent dans un processus daté. Côté hôtels et photographes, un actif premium reste non monétisé.",
  bullets: [
    { icon: "SearchX", title: "Photos introuvables", text: "Le client ne retrouve jamais les clichés sur lesquels il apparaît." },
    { icon: "Building2", title: "Hôtels sans levier", text: "Aucun canal structuré pour transformer les photos en revenu." },
    { icon: "Camera", title: "Photographes limités", text: "Vente artisanale, logistique lourde, faible conversion." },
  ],
  image: IMG.photographer,
  imageAlt: "Photographe en événement",
  notes:
    "Version condensée : présentez le double problème (émotion + économie) en une seule slide, puis enchaînez vite sur la solution.",
};

const s_journey_merged = {
  type: "steps",
  accent: "gold",
  overline: "Les parcours",
  title: "Client et photographe,\nune même fluidité.",
  steps: [
    { n: "1", title: "Le client scanne", text: "QR ou lien, puis un selfie pour retrouver ses photos." },
    { n: "2", title: "Sa galerie s'ouvre", text: "Sélection personnalisée, paiement et livraison HD." },
    { n: "3", title: "Le photographe publie", text: "Upload et indexation faciale automatiques." },
    { n: "4", title: "La vente s'opère", text: "iFotoLife gère vitrine, paiement et reversements." },
  ],
  mockup: "phone",
  mockImage: IMG.familyBeach,
  notes:
    "Fusion des deux parcours : montrez la symétrie de l'expérience côté client et côté photographe.",
};

const full = [
  s_title,
  s_problem_client,
  s_problem_hotels,
  s_solution,
  s_journey_client,
  s_journey_photographer,
  s_value_hotels,
  s_features,
  s_business,
  s_usecases,
  s_differentiation,
  s_vision,
  s_faq,
  s_cta,
];

const short = [
  s_title,
  s_problem_merged,
  s_solution,
  s_journey_merged,
  s_value_hotels,
  s_features,
  s_business,
  s_usecases,
  s_differentiation,
  s_cta,
];

// ---- Tutoriel utilisateur (8) ----
const tutorial = [
  {
    type: "tutorial", accent: "gold", step: "01", overline: "Étape 1",
    title: "Bienvenue\nsur iFotoLife.",
    body: "Retrouvez et téléchargez les photos de votre séjour en quelques secondes. Suivez le guide.",
    mockup: "phone", mockImage: IMG.familyWatermelon, mockTitle: "iFotoLife",
    notes: "Rassurez : l'expérience est simple et rapide. Donnez envie de commencer.",
  },
  {
    type: "tutorial", accent: "gold", step: "02", overline: "Étape 2",
    title: "Trouvez\nvotre lieu.",
    body: "Scannez le QR code de l'établissement ou ouvrez le lien reçu pour accéder à votre événement.",
    mockup: "phone", mockImage: IMG.resortActivities, mockTitle: "Votre séjour",
    notes: "Montrez les deux entrées possibles : QR sur place, ou lien à distance.",
  },
  {
    type: "tutorial", accent: "gold", step: "03", overline: "Étape 3",
    title: "Lancez\nla recherche.",
    body: "Utilisez la reconnaissance faciale pour retrouver vos photos, ou parcourez librement la galerie de l'événement.",
    mockup: "phone", mockImage: IMG.familyPool, mockTitle: "Recherche",
    notes: "Deux options : reconnaissance faciale (rapide) ou navigation galerie (libre).",
  },
  {
    type: "tutorial", accent: "gold", step: "04", overline: "Étape 4",
    title: "Prenez\nvotre selfie.",
    body: "Un simple selfie suffit : iFotoLife identifie instantanément toutes les photos où vous apparaissez.",
    mockup: "phone", mockImage: IMG.familyPool, mockTitle: "Selfie",
    notes: "Insistez sur la simplicité : un selfie, et c'est fait. Données utilisées uniquement pour la recherche.",
  },
  {
    type: "tutorial", accent: "gold", step: "05", overline: "Étape 5",
    title: "Découvrez\nvotre galerie.",
    body: "Toutes vos photos, réunies dans une galerie personnalisée, prêtes à être parcourues.",
    mockup: "phone", mockImage: IMG.familyPoolDad, mockTitle: "Ma galerie",
    notes: "Moment de plaisir : le client redécouvre ses souvenirs, joliment présentés.",
  },
  {
    type: "tutorial", accent: "gold", step: "06", overline: "Étape 6",
    title: "Sélectionnez\net ajoutez au panier.",
    body: "Choisissez vos préférées et ajoutez-les au panier en un geste.",
    mockup: "phone", mockImage: IMG.brandPhotographer, mockTitle: "Panier",
    notes: "Rappelez la simplicité de sélection : un appui pour ajouter, aperçu clair.",
  },
  {
    type: "tutorial", accent: "gold", step: "07", overline: "Étape 7",
    title: "Payez\nen toute sécurité.",
    body: "Réglez en quelques secondes via un paiement sécurisé et rassurant.",
    mockup: "payment", mockTitle: "Paiement",
    notes: "Rassurez sur la sécurité du paiement. Transaction fluide et conforme.",
  },
  {
    type: "tutorial", accent: "gold", step: "08", overline: "Étape 8",
    title: "Téléchargez\nvos photos HD.",
    body: "Vos souvenirs en haute définition, disponibles immédiatement. À vous de les partager.",
    mockup: "phone", mockImage: IMG.familyWatermelon, mockTitle: "Téléchargement",
    notes: "Concluez sur la satisfaction : le souvenir est livré, en HD, tout de suite.",
  },
];

const onepager = {
  eyebrow: "One-page commerciale",
  title: "iFotoLife",
  slogan: "Vos souvenirs, retrouvés en un regard.",
  intro:
    "iFotoLife est la marketplace photo premium des hôtels, resorts et événements. Grâce à la reconnaissance faciale, chaque client retrouve ses photos en un selfie et les télécharge en HD — générant un revenu additionnel clé en main pour l'établissement et le photographe.",
  problemTitle: "Le problème",
  problem:
    "Les photos de séjour se perdent dans un processus daté ; hôtels et photographes ne disposent d'aucun outil pour monétiser simplement cet actif premium.",
  solutionTitle: "La solution",
  solution:
    "Une plateforme clé en main : reconnaissance faciale, galerie personnalisée, watermark pro, paiement sécurisé et livraison HD. Trois gagnants, une seule expérience.",
  argsTitle: "5 arguments clés",
  args: [
    "Reconnaissance faciale instantanée — plus jamais de photos perdues.",
    "Revenu additionnel clé en main pour hôtels & resorts.",
    "Ventes structurées et automatisées pour les photographes.",
    "Expérience client premium alignée sur les standards hospitality.",
    "Paiement sécurisé + livraison HD + watermark pro.",
  ],
  statsTitle: "Chiffres clés",
  stats: [
    { value: "+15 %", label: "de revenu annexe" },
    { value: "< 3 s", label: "pour retrouver ses photos" },
    { value: "0 €", label: "d'investissement initial" },
    { value: "3", label: "langues : FR · EN · ES" },
  ],
  audienceTitle: "Pour qui",
  audience: ["Hôtels & resorts", "Photographes partenaires", "Investisseurs & partenaires", "Clients finaux"],
  ctaTitle: "Devenons partenaires",
  cta:
    "Activons ensemble un nouveau revenu premium pour votre établissement. Contact : contact@ifotolife.shop · ifotolife.shop",
};

export const fr = { ui, home, modeMeta, decks: { full, short, tutorial }, onepager };
